import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { BillingStatusResponse } from '../../data/ServerApiClient'
import { ServerApiClient } from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'
import { openCfoBillingPortal, startCfoCheckout } from '../parity-dev/cfo/cfoBilling'
import { CfoBrandMark, navigateTo } from './CfoSiteShared'
import './CfoSite.css'

const money = (cents: number | null | undefined) =>
  cents == null ? '—' : new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(cents / 100)

export function CfoPartnerBillingPage({ api: apiProp }: { api?: ServerApiClient } = {}) {
  const api = useMemo(() => apiProp ?? new ServerApiClient(), [apiProp])
  const workbookId = useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('workbook_id')?.trim() || '' } catch { return '' }
  }, [])
  const [status, setStatus] = useState<BillingStatusResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const session = await ensureApiSessionReady()
      if (!session.token) {
        window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)
        return
      }
      if (!workbookId) throw new Error('Open Partner billing from CFO Experiences or the Firm Cockpit.')
      setStatus(await api.billingStatus(workbookId))
    } catch (err: any) {
      setError(String(err?.message || err || 'Could not load billing.'))
    } finally {
      setLoading(false)
    }
  }, [api, workbookId])

  useEffect(() => { void load() }, [load])

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key)
    setError('')
    try { await action() } catch (err: any) {
      setError(String(err?.message || err || 'Billing action failed.'))
      setBusy('')
    }
  }

  const switchPartner = async () => {
    if (!window.confirm('Switch this subscription to Partner billing now? Stripe will prorate the change immediately.')) return
    setBusy('switch')
    setError('')
    try {
      setStatus(await api.switchToPartnerBilling(workbookId))
      setBusy('')
    } catch (err: any) {
      setError(String(err?.message || err || 'Could not switch the subscription.'))
      setBusy('')
    }
  }

  const entitled = ['active', 'trialing', 'past_due'].includes(status?.plan_status || '')
  const isPartner = status?.plan_code === 'partner'
  const hasSubscription = status?.has_subscription === true
  const switchableSubscription = hasSubscription && ['active', 'trialing', 'past_due', 'incomplete'].includes(status?.plan_status || '')
  const partnerSwitchEnabled = status?.can_switch_to_partner === true
  const quantity = Math.max(1, status?.billable_client_count || 0)

  return (
    <div className="cfo-site">
      <nav className="cfo-site-nav">
        <span onClick={() => navigateTo('/')} style={{ cursor: 'pointer' }}><CfoBrandMark sub="Partner billing" /></span>
        <button className="cfo-btn cfo-btn-ghost" onClick={() => navigateTo('/')}>Back to CFO Experiences</button>
      </nav>
      <main className="cfo-center" style={{ alignItems: 'flex-start', paddingTop: 36 }}>
        <section className="cfo-panel" style={{ width: 'min(760px, 100%)' }}>
          <span className="cfo-eyebrow">Wholesale partner plan</span>
          <h1 style={{ marginBottom: 8 }}>Billing follows your provisioned client book.</h1>
          <p className="sub">One monthly subscription for the firm. Every provisioned CFO workspace counts as one client; temporary provider disconnects do not change billing.</p>

          {loading ? <p>Loading billing…</p> : null}
          {error ? <div className="cfo-error">{error}</div> : null}
          {status ? (
            <>
              <div className="cfo-plans" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', marginTop: 24 }}>
                <div className="cfo-card"><div className="cfo-eyebrow">Provisioned clients</div><div className="num" style={{ fontSize: 34, fontWeight: 800 }}>{status.billable_client_count}</div><p className="sub">Billed quantity: {hasSubscription && isPartner ? status.subscription_quantity ?? 'syncing' : quantity}</p></div>
                <div className="cfo-card"><div className="cfo-eyebrow">Wholesale rate</div><div className="num" style={{ fontSize: 34, fontWeight: 800 }}>{money(status.partner_unit_amount_cents)}</div><p className="sub">per client / month</p></div>
                <div className="cfo-card"><div className="cfo-eyebrow">Estimated monthly</div><div className="num" style={{ fontSize: 34, fontWeight: 800 }}>{money(status.estimated_monthly_cents)}</div><p className="sub">{status.next_tier_at ? `Next rate at ${status.next_tier_at} clients` : 'Lowest published rate'}</p></div>
              </div>

              {isPartner && switchableSubscription ? (
                <div className="cfo-card" style={{ marginTop: 18 }}>
                  <h3>Partner subscription · {status.plan_status || 'unknown'}</h3>
                  <p className="sub">Quantity reconciliation: {status.quantity_sync_status === 'synced' ? 'up to date' : status.quantity_sync_status}. Client changes are prorated immediately.</p>
                  <button className="cfo-btn cfo-btn-primary" disabled={busy !== ''} onClick={() => run('portal', () => openCfoBillingPortal(api, workbookId))}>{busy === 'portal' ? 'Opening…' : 'Manage payment, invoices, or cancellation'}</button>
                </div>
              ) : switchableSubscription && partnerSwitchEnabled ? (
                <div className="cfo-card" style={{ marginTop: 18 }}>
                  <h3>Switch your existing {status.plan_code || 'standard'} subscription</h3>
                  <p className="sub">The single subscription item will move to Partner pricing at quantity {quantity}. Stripe will invoice the prorated difference immediately.</p>
                  <button className="cfo-btn cfo-btn-primary" disabled={busy !== ''} onClick={() => void switchPartner()}>{busy === 'switch' ? 'Switching…' : 'Switch to Partner'}</button>
                </div>
              ) : switchableSubscription ? (
                <div className="cfo-card" style={{ marginTop: 18 }}>
                  <h3>Your current subscription will stay unchanged</h3>
                  <p className="sub">Partner conversion is not enabled for this billing account. Contact FuseGrid if the firm should move from its existing individual plan.</p>
                </div>
              ) : (
                <div className="cfo-card" style={{ marginTop: 18 }}>
                  <h3>{entitled ? 'Continue on Partner' : 'Start Partner billing'}</h3>
                  <p className="sub">Checkout starts at quantity {quantity}. Partner billing is monthly only and promotion codes do not apply.</p>
                  <button className="cfo-btn cfo-btn-primary" disabled={busy !== ''} onClick={() => run('checkout', () => startCfoCheckout(api, workbookId, 'partner', 'monthly'))}>{busy === 'checkout' ? 'Starting checkout…' : `Continue — ${money(status.estimated_monthly_cents)}/mo`}</button>
                </div>
              )}
            </>
          ) : null}
        </section>
      </main>
    </div>
  )
}

export default CfoPartnerBillingPage
